import type { FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InternalMetrics } from "@moxipass/shared";
import {
  AppShell,
  Button,
  ButtonLink,
  DataTable,
  Field,
  MetricStrip,
  PageHeader,
  ProgressMeter,
  StatusPill,
  Surface,
  TurnstileChallenge
} from "@moxipass/ui";
import { api } from "./api";

const nav = [
  { href: "/", label: "Command", icon: "solar:chart-square-bold-duotone" },
  { href: "/merchants", label: "Merchants", icon: "solar:shop-bold-duotone" },
  { href: "/audit", label: "Audit", icon: "solar:clipboard-check-bold-duotone" },
  { href: "/support", label: "Support", icon: "solar:help-bold-duotone" }
];

const emptyMetrics: InternalMetrics = {
  activeMerchants: 0,
  trialMerchants: 0,
  dailyJoins: 0,
  redemptionsToday: 0,
  failedRedemptions: 0,
  queueBacklog: 0,
  failedJobs: 0,
  d1ShardUsage: []
};
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

function activeRoute() {
  return `/${window.location.pathname.split("/").filter(Boolean)[0] ?? ""}`;
}

function toneFor(status: string) {
  if (status === "active") return "green" as const;
  if (status === "trial") return "blue" as const;
  if (status === "past_due") return "amber" as const;
  return "red" as const;
}

function formatTimestamp(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

export function App() {
  const current = activeRoute();
  const activeNav = nav.map((item) => ({ ...item, active: item.href === current || (item.href === "/" && current === "/") }));
  return (
    <AppShell appName="MoxiPass" eyebrow="Internal admin" nav={activeNav}>
      {current === "/" ? <Command /> : null}
      {current === "/merchants" ? <Merchants /> : null}
      {current === "/audit" ? <Audit /> : null}
      {current === "/support" ? <Support /> : null}
    </AppShell>
  );
}

function Command() {
  const metrics = useQuery({
    queryKey: ["internal", "metrics"],
    queryFn: api.metrics
  });
  const data = metrics.data ?? emptyMetrics;
  const largestShard = data.d1ShardUsage.reduce(
    (largest, shard) => (shard.sizeMb > largest.sizeMb ? shard : largest),
    { shardId: "op_001", sizeMb: 0, thresholdMb: 7000 }
  );
  const shardPercent = largestShard.thresholdMb ? Math.round((largestShard.sizeMb / largestShard.thresholdMb) * 1000) / 10 : 0;

  return (
    <>
      <PageHeader title="Founder operations" kicker="Internal command" />
      <MetricStrip
        items={[
          { label: "Active merchants", value: data.activeMerchants, detail: `${data.trialMerchants} trials`, tone: "green" },
          { label: "Daily joins", value: data.dailyJoins, detail: "Customer joins", tone: "blue" },
          { label: "Redemptions", value: data.redemptionsToday, detail: `${data.failedRedemptions} failed`, tone: data.failedRedemptions ? "amber" : "green" },
          { label: "Failed jobs", value: data.failedJobs, detail: "Needs review", tone: data.failedJobs ? "red" : "green" }
        ]}
      />
      <Surface title="D1 shard usage" icon="solar:database-bold-duotone">
        {data.d1ShardUsage.map((shard) => (
          <ProgressMeter key={shard.shardId} value={shard.sizeMb} max={shard.thresholdMb} label={`${shard.shardId} MB before split trigger`} />
        ))}
      </Surface>
      <section className="grid-two">
        <Surface title="Cost and usage" icon="solar:wallet-money-bold-duotone">
          <ul className="list">
            <li className="list-row">
              <span>D1 storage</span>
              <strong>{largestShard.sizeMb} MB · {shardPercent}%</strong>
            </li>
            <li className="list-row">
              <span>Queue backlog</span>
              <strong>{data.queueBacklog}</strong>
            </li>
            <li className="list-row">
              <span>Billable merchants</span>
              <strong>{data.activeMerchants}</strong>
            </li>
          </ul>
        </Surface>
        <Surface title="Founder alerts" icon="solar:bell-bing-bold-duotone">
          <ul className="list">
            <li className="list-row"><span>Redemption failure spike</span><StatusPill tone="amber">Watch</StatusPill></li>
            <li className="list-row"><span>D1 6 GB threshold</span><StatusPill tone="green">OK</StatusPill></li>
            <li className="list-row"><span>Failed jobs</span><StatusPill tone="green">Clear</StatusPill></li>
          </ul>
        </Surface>
        <Surface title="Recovery posture" icon="solar:shield-warning-bold-duotone">
          <ul className="list">
            <li className="list-row"><span>D1 Time Travel</span><strong>Documented</strong></li>
            <li className="list-row"><span>R2 exports</span><strong>Private bucket</strong></li>
            <li className="list-row"><span>Audit visibility</span><strong>Enabled</strong></li>
          </ul>
        </Surface>
      </section>
    </>
  );
}

function Merchants() {
  const merchants = useQuery({ queryKey: ["internal", "merchants"], queryFn: api.merchants, placeholderData: [] });
  const rows = merchants.data ?? [];
  return (
    <>
      <PageHeader title="Merchants" kicker="Account control" />
      <Surface title="Merchant list" icon="solar:shop-bold-duotone">
        <DataTable
          columns={["Name", "Status", "Plan", "Members", "Shard", "Renewal"]}
          rows={rows.map((merchant) => [
            <strong>{merchant.name}</strong>,
            <StatusPill tone={toneFor(merchant.status)}>{merchant.status}</StatusPill>,
            merchant.plan,
            merchant.activeMembers,
            merchant.shardId,
            merchant.renewalDate ?? "n/a"
          ])}
        />
      </Surface>
    </>
  );
}

function Audit() {
  const audit = useQuery({ queryKey: ["internal", "audit"], queryFn: api.audit, placeholderData: [] });
  const rows = audit.data ?? [];
  return (
    <>
      <PageHeader title="Audit logs" kicker="Sensitive actions" />
      <Surface title="Recent activity" icon="solar:clipboard-check-bold-duotone">
        <DataTable
          columns={["Action", "Actor", "Merchant", "Resource", "Time"]}
          rows={rows.map((row) => [
            row.action,
            `${row.actorType}:${row.actorId}`,
            row.merchantId,
            `${row.resourceType}:${row.resourceId}`,
            formatTimestamp(row.createdAt)
          ])}
        />
      </Surface>
    </>
  );
}

function Support() {
  const queryClient = useQueryClient();
  const session = useQuery({ queryKey: ["auth", "admin-session"], queryFn: api.session, retry: false });
  const merchants = useQuery({ queryKey: ["internal", "merchants"], queryFn: api.merchants, placeholderData: [] });
  const failedJobs = useQuery({ queryKey: ["internal", "failed-jobs"], queryFn: api.failedJobs, placeholderData: { failedJobs: [] } });
  const backups = useQuery({ queryKey: ["internal", "backups"], queryFn: api.backups, placeholderData: { backups: [] } });
  const merchantRows = merchants.data ?? [];
  const failedJobRows = failedJobs.data?.failedJobs ?? [];
  const backupRows = backups.data?.backups ?? [];
  const login = useMutation({
    mutationFn: ({ email, turnstileToken }: { email: string; turnstileToken?: string }) => api.login(email, turnstileToken)
  });
  const logout = useMutation({ mutationFn: api.logout });
  const resolveJob = useMutation({
    mutationFn: api.resolveFailedJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["internal", "failed-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["internal", "metrics"] });
    }
  });
  const status = useMutation({
    mutationFn: ({ merchantId, merchantStatus }: { merchantId: string; merchantStatus: string }) =>
      api.updateMerchantStatus(merchantId, merchantStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["internal", "merchants"] });
      queryClient.invalidateQueries({ queryKey: ["internal", "audit"] });
    }
  });
  const defaultMerchantId = merchantRows[0]?.id ?? "";

  function submitStatus(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = Object.fromEntries(new FormData(event.currentTarget));
    status.mutate({ merchantId: String(form.merchantId), merchantStatus: String(form.status) });
  }

  function submitLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = Object.fromEntries(new FormData(event.currentTarget));
    login.mutate({
      email: String(form.email),
      turnstileToken: String(form.turnstileToken ?? "")
    });
  }

  return (
    <>
      <PageHeader title="Support tools" kicker="Founder actions" />
      <section className="grid-two">
        <Surface title="Admin session" icon="solar:login-3-bold-duotone">
          <form className="form-grid" onSubmit={submitLogin}>
            <Field label="Admin email"><input name="email" type="email" defaultValue="admin@moxipass.example" /></Field>
            <TurnstileChallenge siteKey={TURNSTILE_SITE_KEY} action="admin_login" />
            <Button type="submit" icon="solar:login-3-bold-duotone" disabled={login.isPending}>Sign in</Button>
            <Button type="button" variant="secondary" icon="solar:logout-3-bold-duotone" onClick={() => logout.mutate()} disabled={logout.isPending}>Logout</Button>
          </form>
          <div className="detail-row">
            <span>Current session</span>
            <strong>{login.data?.userId ?? session.data?.userId ?? (session.isPending ? "Checking..." : "No active admin session")}</strong>
          </div>
        </Surface>
        <Surface title="Merchant status" icon="solar:power-bold-duotone">
          <form className="form-grid" onSubmit={submitStatus}>
            <Field label="Merchant ID"><input key={defaultMerchantId} name="merchantId" defaultValue={defaultMerchantId} /></Field>
            <Field label="Status"><select name="status" defaultValue="active"><option value="active">Active</option><option value="suspended">Suspended</option><option value="past_due">Past due</option></select></Field>
            <Button type="submit" icon="solar:shield-keyhole-bold-duotone" disabled={status.isPending || !defaultMerchantId}>Apply with audit</Button>
          </form>
          {status.data ? <p className="tone-green">{status.data.merchantId} set to {status.data.status}</p> : null}
        </Surface>
        <Surface title="Failed jobs" icon="solar:danger-triangle-bold-duotone">
          {failedJobRows.length ? (
            <ul className="list">
              {failedJobRows.map((job) => (
                <li className="list-row" key={job.id}>
                  <div>
                    <strong>{job.messageType}</strong>
                    <span>{job.queueName} · {job.lastError}</span>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    icon="solar:check-circle-bold-duotone"
                    onClick={() => resolveJob.mutate(job.id)}
                    disabled={resolveJob.isPending}
                  >
                    Resolve
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="list">
              <li className="list-row"><span>Dead-letter jobs</span><StatusPill tone="green">0</StatusPill></li>
              <li className="list-row"><span>Replay safety</span><strong>Idempotent</strong></li>
              <li className="list-row"><span>Export cleanup</span><strong>Scheduled</strong></li>
            </ul>
          )}
        </Surface>
        <Surface title="D1 backups" icon="solar:database-bold-duotone">
          <ul className="list">
            {backupRows.length ? (
              backupRows.map((backup) => (
                <li className="list-row" key={backup.id}>
                  <div>
                    <strong>{backup.jobDate}</strong>
                    <span>{backup.shardId} · {backup.sizeBytes ? `${Math.ceil(backup.sizeBytes / 1024)} KB` : backup.status}</span>
                  </div>
                  {backup.status === "ready" ? (
                    <ButtonLink href={api.backupDownloadUrl(backup.id)} variant="secondary" icon="solar:download-minimalistic-bold-duotone">
                      SQLite
                    </ButtonLink>
                  ) : (
                    <StatusPill tone={backup.status === "failed" ? "red" : "amber"}>{backup.status}</StatusPill>
                  )}
                </li>
              ))
            ) : (
              <li className="list-row"><span>Scheduled backups</span><StatusPill tone="amber">Pending</StatusPill></li>
            )}
          </ul>
        </Surface>
      </section>
    </>
  );
}
